/**
 * useOracles.ts
 *
 * Hook for reading oracle registry and consensus data from the
 * PredictionMarketMultiOracle contract.
 *
 * NOTE: Requires REACT_APP_MULTI_ORACLE_ADDRESS to be set.
 */

import { useState, useCallback } from 'react';
import { ethers } from 'ethers';

// ── ABI fragment ──────────────────────────────────────────────────────────────

const MULTI_ORACLE_ABI = [
  'function getOracleList() view returns (address[])',
  'function oracles(address) view returns (bool)',
  'function quorum() view returns (uint256)',
  'function disputeWindowSeconds() view returns (uint256)',
  'function getVote(uint256 marketId, address oracle) view returns (tuple(bool outcome, uint256 price, uint256 timestamp, bool exists))',
  'function getMarketState(uint256 marketId) view returns (tuple(uint256 yesVotes, uint256 noVotes, bool resolved, bool outcome, uint256 settledPrice, uint256 resolvedAt, bool disputed))',
];

// ── Config ────────────────────────────────────────────────────────────────────

const MULTI_ORACLE_ADDRESS =
  process.env.REACT_APP_MULTI_ORACLE_ADDRESS || '';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface OracleInfo {
  address:  string;
  isActive: boolean;
}

export interface OracleVote {
  oracle:    string;
  outcome:   boolean;
  price:     string;
  timestamp: number;
  exists:    boolean;
}

export interface ConsensusData {
  marketId:     number;
  yesVotes:     number;
  noVotes:      number;
  quorum:       number;
  resolved:     boolean;
  outcome:      boolean;
  disputed:     boolean;
  oracleVotes:  OracleVote[];
}

export interface UseOraclesResult {
  oracles:          OracleInfo[];
  quorum:           number;
  loading:          boolean;
  fetchOracles:     () => Promise<void>;
  fetchConsensus:   (marketId: number) => Promise<ConsensusData | null>;
  isConfigured:     boolean;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getProvider(): ethers.BrowserProvider {
  const eth = (window as any).ethereum;
  if (!eth) throw new Error('MetaMask not detected. Please install MetaMask.');
  return new ethers.BrowserProvider(eth);
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useOracles(): UseOraclesResult {
  const [oracles,  setOracles]  = useState<OracleInfo[]>([]);
  const [quorum,   setQuorum]   = useState(0);
  const [loading,  setLoading]  = useState(false);

  const isConfigured = Boolean(MULTI_ORACLE_ADDRESS);

  const fetchOracles = useCallback(async (): Promise<void> => {
    if (!isConfigured) {
      console.warn('[useOracles] REACT_APP_MULTI_ORACLE_ADDRESS not set');
      return;
    }

    setLoading(true);
    try {
      const provider = getProvider();
      const contract = new ethers.Contract(MULTI_ORACLE_ADDRESS, MULTI_ORACLE_ABI, provider);

      const [addresses, quorumRaw]: [string[], bigint] = await Promise.all([
        contract.getOracleList(),
        contract.quorum(),
      ]);

      const details: OracleInfo[] = await Promise.all(
        addresses.map(async addr => ({
          address:  addr,
          isActive: await contract.oracles(addr) as boolean,
        })),
      );

      setOracles(details);
      setQuorum(Number(quorumRaw));
    } catch (err) {
      console.error('[useOracles] fetchOracles error:', err);
    } finally {
      setLoading(false);
    }
  }, [isConfigured]);

  const fetchConsensus = useCallback(async (marketId: number): Promise<ConsensusData | null> => {
    if (!isConfigured) return null;

    try {
      const provider = getProvider();
      const contract = new ethers.Contract(MULTI_ORACLE_ADDRESS, MULTI_ORACLE_ABI, provider);

      const [state, addresses, quorumRaw]: [any, string[], bigint] = await Promise.all([
        contract.getMarketState(BigInt(marketId)),
        contract.getOracleList(),
        contract.quorum(),
      ]);

      const oracleVotes: OracleVote[] = await Promise.all(
        addresses.map(async addr => {
          const v = await contract.getVote(BigInt(marketId), addr);
          return {
            oracle:    addr,
            outcome:   v.outcome as boolean,
            price:     v.price.toString() as string,
            timestamp: Number(v.timestamp),
            exists:    v.exists as boolean,
          };
        }),
      );

      return {
        marketId,
        yesVotes:    Number(state.yesVotes),
        noVotes:     Number(state.noVotes),
        quorum:      Number(quorumRaw),
        resolved:    state.resolved,
        outcome:     state.outcome,
        disputed:    state.disputed,
        oracleVotes: oracleVotes.filter(v => v.exists),
      };
    } catch (err) {
      console.error('[useOracles] fetchConsensus error:', err);
      return null;
    }
  }, [isConfigured]);

  return {
    oracles,
    quorum,
    loading,
    fetchOracles,
    fetchConsensus,
    isConfigured,
  };
}
